/*
 * Copyright Mimic Networks, Inc. 2025.
 */

import { debounce } from 'lodash';
import { useCallback, useMemo, useState } from 'react';

import { Node } from '@/client';
import { useMessage } from '@/hooks/message';
import { useUpdateNodeMutation } from '@/hooks/mutation/useUpdateNodeMutation';
import { usePaginatedSubtenantsQuery } from '@/hooks/query/usePaginatedSubtenantsQuery';
import { useMimicTranslation } from '@/hooks/useMimicTranslation';
import { Select, SelectProps } from '@/primitives/Select';
import { useMgmtPlaneStateStore } from '@/state/mgmtPlaneStore';

import { SubtenantSelectLabel } from './SubtenantSelectLabel';

export type NodeSubtenantSelectProps = {
  node?: Node;
};

export function NodeSubtenantSelect({ node }: NodeSubtenantSelectProps) {
  const { t } = useMimicTranslation('subtenants');
  const { t: nodeT } = useMimicTranslation('nodes');
  const [message] = useMessage();
  const selectedTenantID = useMgmtPlaneStateStore((state) => state.selectedTenantID);
  const selectedTenantName = useMgmtPlaneStateStore((state) => state.selectedTenantName);
  const [newSubtenantName, setNewSubtenantName] = useState<string | null>(null);
  const [searchValue, setSearchValue] = useState<string>('');
  const [searchInput, setSearchInput] = useState<string>('');

  const { mutate, isPending } = useUpdateNodeMutation({
    nodeId: node?.id || '',
    onError: () => {
      clearSearch();

      message.error(
        nodeT('subtenant.nodeChangedSubtenantFail', {
          oldSubtenant: node?.subtenant?.name || selectedTenantName,
          newSubtenant: newSubtenantName || '',
        }),
      );
    },
    onSuccess: () => {
      message.success(
        nodeT('subtenant.nodeChangedSubtenantSuccess', {
          oldSubtenant: node?.subtenant?.name || selectedTenantName,
          newSubtenant: newSubtenantName,
        }),
      );
    },
    onSettled: () => {
      clearSearch();
    },
  });

  const debouncedSearch = useMemo(
    () =>
      debounce((value: string) => {
        setSearchValue(value);
      }, 300),
    [],
  );

  const handleSearch = useCallback(
    (value: string) => {
      setSearchInput(value);
      debouncedSearch(value);
    },
    [debouncedSearch],
  );

  const clearSearch = useCallback(() => {
    setSearchInput('');
    setSearchValue('');
  }, []);

  const subtenantsQuery = usePaginatedSubtenantsQuery({
    params: {
      flatten: true,
      pageSize: 100,
      filters: searchValue ? { name: searchValue } : undefined,
    },
    type: 'inMemory',
  });

  const renderSubtenantLabel = useCallback(
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    (option: any) => {
      if (typeof option.label !== 'string') return option.label;
      return <SubtenantSelectLabel label={option.label} isLoading={isPending} />;
    },
    [isPending],
  );

  if (!node || !selectedTenantID || !selectedTenantName) return null;

  const tenantOption = {
    label: selectedTenantName,
    value: selectedTenantID,
  };

  const selectOptions: SelectProps['options'] = [tenantOption];
  if (subtenantsQuery.data?.data.length) {
    const subtenantOptions = subtenantsQuery.data?.data.map((w) => ({
      label: w.name,
      value: w.id,
    }));
    selectOptions.push(...subtenantOptions);
  }

  // Hide when there are no subtenants and node isn't assigned to one
  if (!subtenantsQuery.data?.data.length && !node.subtenant?.id) return null;

  return (
    <Select
      options={selectOptions}
      showSearch
      searchValue={searchInput}
      onSearch={handleSearch}
      style={{ width: '400px', maxWidth: '100%', marginRight: '16px' }}
      onChange={(value, option) => {
        const subtenantName = Array.isArray(option) ? String(option[0].label) : String(option?.label);
        if (value) {
          clearSearch();
          if (value === selectedTenantID) {
            setNewSubtenantName(subtenantName);
            mutate({ subtenantId: null });
          } else {
            setNewSubtenantName(subtenantName);
            mutate({ subtenantId: value });
          }
        }
      }}
      filterOption={false}
      notFoundContent={
        subtenantsQuery.isLoading ? t('addSubtenant.parentSubtenantLoading') : t('addSubtenant.parentSubtenantNotFound')
      }
      data-testid="node-subtenant-select"
      value={node.subtenant?.id || selectedTenantID}
      labelRender={renderSubtenantLabel}
    />
  );
}
